import React, { useEffect, useState } from 'react';
import { BASE_URL, USER_URL } from '../../constant/api';
import { BsBoxSeam } from 'react-icons/bs';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

function SellerOfferEdit() {
  const navigate = useNavigate();
  const { offerId } = useParams();
  const [products, setProducts] = useState([]);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [offerData, setOfferData] = useState({
    title: "",
    start_date: "",
    end_date: "",
    banner_image: null,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [existingBannerUrl, setExistingBannerUrl] = useState('');

  // Fetch offer details and products on component load
  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch offer details
        const offerResponse = await USER_URL.get(`/products/seller/special-offer/${offerId}/`);
        if (offerResponse.data) {
          const offer = offerResponse.data.Data.special_offer;
          console.log(offer);
          // Improved date formatting with null checks
          const formatDate = (dateString) => {
            if (!dateString) return "";
            const date = new Date(dateString);
            return isNaN(date.getTime()) ? "" : date.toISOString().split('T')[0];
          };

          console.log(offerResponse);

          setOfferData({
            title: offer.title,
            start_date: formatDate(offer.start_date),
            end_date: formatDate(offer.end_date),
            banner_image: null
          });
          setExistingBannerUrl(offer.banner || "");

          console.log(offerData);

          // Handle offer products
          if (Array.isArray(offerResponse.data.Data?.products)) {
            setSelectedProducts(offerResponse.data.Data.products.map(p => ({
              product: p.id,
              special_discount_percentage: p.special_discount_percentage
            })));
          }
        }

        // Fetch all products
        const productsResponse = await USER_URL.get(`/products/seller/`);
        if (productsResponse.data?.Data) {
          setProducts(productsResponse.data.Data);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
         toast.error("Failed to load offer details. Please try again.");
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, [offerId])

  // Handle form input change for offer data
  const handleOfferChange = (e) => {
    const { name, value } = e.target;
    setOfferData((prevData) => ({
      ...prevData,
      [name]: value
    }));
  };

  // Handle product selection and discount
  const handleProductSelect = (productId, discount = null) => {
    const updatedProducts = [...selectedProducts];
    const productIndex = updatedProducts.findIndex(p => p.product === productId);

    if (productIndex === -1 && discount !== null) {
      updatedProducts.push({ product: productId, special_discount_percentage: parseFloat(discount) || 5 });
    } else if (productIndex === -1) {
      updatedProducts.push({ product: productId, special_discount_percentage: 5 });
    } else if (productIndex !== -1 && !discount) {
      updatedProducts.splice(productIndex, 1);
    } else {
      updatedProducts[productIndex] = {
        product: productId,
        special_discount_percentage: parseFloat(discount) || 5
      };
    }

    setSelectedProducts(updatedProducts);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (isSubmitting) return;

    // Validate required fields
    if (!offerData.title) {
       toast.error("Please enter the offer title.");
      return;
    }
    if (!offerData.start_date) {
       toast.error("Please select a start date.");
      return;
    }
    if (!offerData.end_date) {
       toast.error("Please select an end date.");
      return;
    }
    if (new Date(offerData.end_date) <= new Date(offerData.start_date)) {
      toast.error("End date must be greater than start date.");
      return;
    }
    if (selectedProducts.length === 0) {
       toast.error("Please select at least one product for the offer.");
      return;
    }

    setIsSubmitting(true);
    const formData = new FormData();

    // Append basic fields
    formData.append("title", offerData.title);
    formData.append("start_date", offerData.start_date);
    formData.append("end_date", offerData.end_date);

    // Only append banner if a new image is selected
    if (offerData.banner_image) {
      formData.append("banner", offerData.banner_image);
    }

    // Append each product
    selectedProducts.forEach((p, index) => {
      formData.append(`offer_products[${index}]product`, p.product);
      formData.append(`offer_products[${index}]special_discount_percentage`, p.special_discount_percentage);
    });

    try {
      const response = await USER_URL.put(`/products/seller/special-offer/${offerId}/`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      toast.success("Offer updated successfully!");
      console.log("Response Data:", response.data);
      navigate('/seller/offer_brand/Offer');
    } catch (error) {
      console.error("Error updating offer:", error.response?.data || error.message || error);
      toast.error("Failed to update offer. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Rest of the component remains the same...
  return (
    <div>
      <ToastContainer position="top-center" autoClose={3000} />
      <div className="border-2 rounded-lg bg-gray-50 mt-10 p-2">
        <h2 className="text-green-700 text-3xl font-bold text-center mb-6">
          Edit Offer
        </h2>

        {/* Offer Details Section */}
        <div className="flex flex-col sm:flex-row gap-6 mb-6 w-full">
          {/* Image input on the left side */}
          <div className="w-full sm:w-1/2">
            <label className="text-gray-700 font-medium mb-2 block">Banner Image</label>
            <div className="border rounded-md">
              {offerData.banner_image ? (
                <div className="relative">
                  <img
                    src={URL.createObjectURL(offerData.banner_image)}
                    alt="Banner Preview"
                    className="w-full h-44 object-cover rounded-md mb-4"
                  />
                  <button
                    type="button"
                    className="absolute top-2 right-2 bg-red-500 text-white rounded-full px-2 py-1 text-xs"
                    onClick={() => setOfferData({ ...offerData, banner_image: null })}
                  >
                    Remove
                  </button>
                </div>
              ) : existingBannerUrl ? (
                <div className="relative">
                  <img
                    // src={existingBannerUrl}
                      src={
                        existingBannerUrl
                          ? existingBannerUrl.startsWith(BASE_URL)
                            ? existingBannerUrl
                            : `${BASE_URL}${existingBannerUrl}`
                          : "/images/default-product.png"
                      }
                    alt="Existing Banner"
                    className="w-full h-44 object-cover rounded-md mb-4"
                  />
                </div>
              ) : (
                <div className="w-full h-40 bg-gray-100 flex items-center justify-center rounded-md text-gray-500 mb-4">
                  No Image Selected
                </div>
              )}
              <input
                type="file"
                accept="image/*"
                onChange={(e) => {
                  const file = e.target.files[0];
                  setOfferData({ ...offerData, banner_image: file });
                }}
                className="mb-3 ml-3 text-sm text-gray-600 file:mr-4 file:py-2 file:px-4
           file:rounded-full file:border-0
           file:text-sm file:font-semibold
           file:bg-blue-50 file:text-blue-700
           hover:file:bg-blue-100"
              />
            </div>
          </div>

          {/* Other inputs */}
          <div className="w-full sm:w-1/2 flex flex-col gap-6">
            <div className="w-full">
              <label className="text-gray-700 font-medium mb-2 block">Offer Title</label>
              <input
                type="text"
                name="title"
                value={offerData.title}
                onChange={handleOfferChange}
                className="w-full border p-2 rounded mb-3"
                placeholder="Enter offer title"
              />
            </div>

            <div className="w-full">
              <label className="text-gray-700 font-medium mb-2 block">Start Date</label>
              <input
                type="date"
                name="start_date"
                value={offerData.start_date}
                onChange={handleOfferChange}
                className="w-full border p-2 rounded mb-3"
              />
            </div>

            <div className="w-full">
              <label className="text-gray-700 font-medium mb-2 block">End Date</label>
              <input
                type="date"
                name="end_date"
                value={offerData.end_date}
                onChange={handleOfferChange}
                className="w-full border p-2 rounded"
              />
            </div>

            <div className='w-full'>
              <button
                onClick={handleSubmit}
                className={`w-full mt-6 px-6 py-3 rounded bg-green-600 hover:bg-green-700 text-white ${isSubmitting ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                {isSubmitting ? "Updating..." : "Update Offer"}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Products Table Section */}
      <div className="overflow-x-auto mb-10">
        {isLoading ? (
          <div className="flex justify-center items-center h-40">
            <div className="spinner-border animate-spin inline-block w-8 h-8 border-4 rounded-full text-brand-primary"></div>
            <p className="ml-2">Loading products...</p>
          </div>
        ) : products.length === 0 ? (
          <div className="text-center mt-10">
            <BsBoxSeam className="text-6xl text-gray-400 mx-auto mb-4" />
            <p className="text-lg text-gray-600">No products available</p>
          </div>
        ) : (
          <>
            {/* Table Header */}
            <div className="w-full space-y-4 mt-10">
              <div className="min-w-[800px] grid grid-cols-[4rem,2fr,1.5fr,1.5fr,1fr,2fr,1fr,1fr,1fr] font-medium text-brand-button-green-bg">
                <div className="text-center">Item No</div>
                <div className="text-center">Product Name</div>
                <div className="text-center">Product Code</div>
                <div className="text-center">Offer</div>
                <div className="text-center">Stock</div>
                <div className="text-center">New Discount</div>
                <div className="text-center">Add Offer</div>
                <div className="text-center">View</div>
              </div>
            </div>

            {/* Table Body */}
            {products.map((product, index) => (
              <div key={product.id} className="grid text-[#2F2E41] min-w-[800px] grid-cols-[4rem,2fr,1.5fr,1.5fr,1fr,2fr,1fr,1fr,1fr] items-center gap-2 py-2">
                <div className="rounded-xl border p-2 text-center line-clamp-1 hover:line-clamp-none">{index + 1}</div>
                <div className="rounded-xl border p-2 text-center line-clamp-1 hover:line-clamp-none">{product.name}</div>
                <div className="rounded-xl border p-2 text-center line-clamp-1 hover:line-clamp-none">{product.product_code}</div>
                <div className="rounded-xl border p-2 text-center line-clamp-1 hover:line-clamp-none">{product.offer_percent ? `${product.offer_percent}%` : 'No Offer'}</div>
                <div className="rounded-xl border p-2 text-center line-clamp-1 hover:line-clamp-none">{product.stock}</div>
                <input
                  type="number"
                  value={selectedProducts.find((item) => item.product === product.id)?.special_discount_percentage || ''}
                  onChange={(e) => handleProductSelect(product.id, e.target.value)}
                  className="rounded-xl border p-2 text-center"
                />
                <div className="text-center">
                  <div
                    onClick={() => handleProductSelect(product.id)}
                    className={`rounded-xl p-2 text-center cursor-pointer transition-all duration-300 
                      ${selectedProducts.find((item) => item.product === product.id)
                        ? 'border-red-500 bg-red-100 text-red-600 hover:bg-red-200'
                        : 'border-blue-500 bg-blue-100 text-blue-600 hover:bg-blue-200'}`}
                  >
                    {selectedProducts.find((item) => item.product === product.id) ? 'Remove' : 'Add'}
                  </div>
                </div>
                <div className="border p-2 rounded-xl text-center hover:bg-brand-primary text-brand-text-light hover:text-white transition ease-in-out duration-400">
                  <Link to={`/seller/products/view/${product.id}`}>View</Link>
                </div>
              </div>
            ))}
          </>
        )}
      </div>
    </div>
  );
}

export default SellerOfferEdit;